import { Button, CircularProgress, Container } from '@montugroup/design-system';
import { styled } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import type { ErrorProps } from '@/components/error/Error';
import Error from '@/components/error/Error';
import UnauthenticatedLayout from '@/components/layout/UnauthenticatedLayout';
import settings from '@/constants/constants';
import { LOGIN_ROUTE, PATIENT_REFILL_ROUTE, PAYMENT_SUCCESS_ROUTE } from '@/routes/route-paths';
import { Logger } from '@/utils/logger';
import { useSession } from '@montugroup/circuit-shared';
import { PaymentService } from '@montugroup/circuit-shared/services/payment.service';

const logger = new Logger('PayByInvoice');

const ContentContainer = styled(Container)`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

function PayByInvoice() {
  const { status } = useSession();
  const params = useParams<{ token: string }>();
  const [error, setError] = useState<boolean | ErrorProps>(false);
  const navigate = useNavigate();

  const handleRefillClick = () => {
    if (status !== 'authenticated') {
      navigate(LOGIN_ROUTE, { state: { from: { pathname: PATIENT_REFILL_ROUTE } } });
    } else {
      navigate(PATIENT_REFILL_ROUTE);
    }
  };

  const getCheckoutRedirectUrlForUrl = async () => {
    setError(false);
    const { token } = params;
    if (!token) {
      // eslint-disable-next-line no-console
      console.error('no token for getCheckoutRedirectUrlForUrl');
      return;
    }
    const {
      checkoutRedirectUrl,
      orderCode,
      error: responseError,
      isPaid,
      paidOrderAttributes,
      userStatus,
      isUnavailable,
    } = await PaymentService.getCheckoutRedirectUrlForInvoice(token);

    if (isPaid) {
      navigate(PAYMENT_SUCCESS_ROUTE, {
        state: {
          orderCode,
          firstName: paidOrderAttributes?.userFirstName,
          email: paidOrderAttributes?.userEmail,
          onBoardKey: paidOrderAttributes?.onBoardKey,
        },
      });
    } else if (checkoutRedirectUrl) {
      window.location.replace(checkoutRedirectUrl);
    } else if (isUnavailable) {
      setError({
        message: 'This order is no longer valid, please visit the Refill page to place a new order',
        buttons: [
          // eslint-disable-next-line react/jsx-key
          <Button variant="contained" onClick={handleRefillClick}>
            Refill medication
          </Button>,
        ],
      });
    } else if (userStatus?.isDischarged) {
      setError({
        message: 'You have been discharged. Please contact our support team.',
        buttons: [
          // eslint-disable-next-line react/jsx-key
          <Button variant="contained" href={`${settings.support.site.url}/requests/new`}>
            Contact support
          </Button>,
        ],
      });
    } else if (userStatus?.isUserActive === false) {
      setError({
        message: 'Your account is not active. Please contact our support team.',
        buttons: [
          // eslint-disable-next-line react/jsx-key
          <Button variant="contained" href={`${settings.support.site.url}/requests/new`}>
            Contact support
          </Button>,
        ],
      });
    } else {
      logger.error('Error retrieving checkout redirect URL', responseError);
      setError(true);
    }
  };

  useEffect(() => {
    getCheckoutRedirectUrlForUrl();
  }, []);

  return (
    <UnauthenticatedLayout>
      {!error && <CircularProgress />}
      {error && <ContentContainer>{typeof error === 'boolean' ? <Error /> : <Error {...error} />}</ContentContainer>}
    </UnauthenticatedLayout>
  );
}

export default PayByInvoice;
